import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import CKEditorField from '../components/CKEditorField.jsx';
import { api, uploadUrl } from '../../api/client.js';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN } from '../../lib/ui.js';

const empty = { judul: '', kategori: 'school', deskripsi: '', konten_artikel: '', tanggal: '', is_active: true };

export default function PortfolioForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Di mode Edit, tunggu data dari API dulu supaya CKEditor diinisialisasi dengan konten yang benar
  const [formReady, setFormReady] = useState(!isEdit);
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/portfolio/${id}`).then(({ data }) => {
        setForm({ ...data.item, is_active: !!data.item.is_active, tanggal: data.item.tanggal ? data.item.tanggal.slice(0, 10) : '' });
        setPreview(data.item.image ? uploadUrl(data.item.image) : '');
        setFormReady(true);
      });
      loadGallery();
    }
  }, [id]);

  const loadGallery = () => {
    api.get(`/admin/portfolio/${id}/gallery`)
      .then(({ data }) => setGallery(data.items || []))
      .catch(() => setGallery([]));
  };

  const onGalleryFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Batasi 20 file per batch
    const capped = files.slice(0, 20);
    setGalleryFiles((prev) => [...prev, ...capped]);
    setGalleryPreviews((prev) => [...prev, ...capped.map((f) => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removePendingGallery = (idx) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadGallery = async () => {
    if (!isEdit) {
      Swal2.toast('warning', 'Simpan artikel dulu, baru bisa menambah foto galeri.');
      return;
    }
    if (galleryFiles.length === 0) return;
    setUploadingGallery(true);
    try {
      const fd = new FormData();
      galleryFiles.forEach((f) => fd.append('gallery_images', f));
      const { data } = await api.post(`/admin/portfolio/${id}/gallery`, fd);
      Swal2.toast('success', data.message || 'Foto galeri berhasil diupload.');
      setGalleryFiles([]);
      setGalleryPreviews([]);
      loadGallery();
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal mengupload foto galeri.');
    } finally {
      setUploadingGallery(false);
    }
  };

  const deleteGalleryImage = async (gid) => {
    const isConfirmed = await Swal2.confirmDelete('foto galeri ini');
    if (!isConfirmed) return;
    try {
      await api.delete(`/admin/portfolio/${id}/gallery/${gid}`);
      Swal2.toast('success', 'Foto galeri dihapus.');
      loadGallery();
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal menghapus foto.');
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onFile = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== 'image' && k !== 'id') fd.append(k, v); });
      if (file) fd.append('image', file);
      else if (!preview) fd.append('delete_image', 'true');
      
      if (isEdit) await api.put(`/admin/portfolio/${id}`, fd);
      else await api.post('/admin/portfolio', fd);
      Swal2.toast('success', isEdit ? 'Berhasil diperbarui.' : 'Berhasil ditambahkan.');
      navigate('/admin/portfolio');
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-800 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20';
  const labelCls = 'block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1.5';

  return (
    <AdminLayout title={isEdit ? 'Edit Portfolio' : 'Tambah Portfolio'}>
      <form onSubmit={submit} className="max-w-2xl bg-white dark:bg-neutral-900 rounded-2xl border border-black/5 dark:border-white/5 p-6 space-y-4">
        {error && <div className="p-3 rounded-xl text-sm font-semibold bg-red-50 text-red-600 dark:bg-red-950/30">{error}</div>}

        <div>
          <label className={labelCls}>Judul</label>
          <input required className={inputCls} value={form.judul} onChange={set('judul')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Kategori</label>
            <select className={inputCls} value={form.kategori} onChange={set('kategori')}>
              <option value="school">School</option>
              <option value="expo">Expo</option>
              <option value="contest">Contest</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Tanggal</label>
            <input type="date" className={inputCls} value={form.tanggal || ''} onChange={set('tanggal')} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Deskripsi Singkat</label>
          <textarea rows={3} className={`${inputCls} resize-none`} value={form.deskripsi || ''} onChange={set('deskripsi')} />
        </div>
        <div>
          <label className={labelCls}>Konten Artikel (opsional, tampil di halaman detail)</label>
          {formReady ? (
            <CKEditorField
              key={isEdit ? id : 'new'}
              initialValue={form.konten_artikel || ''}
              onChange={(html) => setForm((f) => ({ ...f, konten_artikel: html }))}
              placeholder="Tulis artikel lengkap tentang event ini..."
            />
          ) : (
            <div className="w-full h-40 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 animate-pulse" />
          )}
          <p className="text-[11px] text-neutral-400 mt-1.5">Jika diisi, tombol "Baca Artikel" akan tampil di portofolio dan membuka halaman gaya artikel berita.</p>
        </div>
        <div>
          <label className={labelCls}>Gambar Utama</label>
          {preview && (
            <div className="relative group w-full h-40 mb-2">
              <img src={preview} className="w-full h-40 object-cover rounded-xl" />
              <button
                type="button"
                onClick={() => { setPreview(''); setFile(null); }}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Hapus gambar utama"
              >
                ✕
              </button>
            </div>
          )}
          <input type="file" accept="image/*" onChange={onFile} className="text-sm text-neutral-500" />
        </div>

        {/* Galeri Foto Artikel */}
        <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4">
          <label className={labelCls}>Galeri Foto Dokumentasi (opsional)</label>
          <p className="text-[11px] text-neutral-400 mb-2">Kumpulan foto dokumentasi kegiatan yang tampil di bawah isi artikel. Hanya aktif di mode Edit setelah artikel disimpan.</p>

          {gallery.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {gallery.map((g) => (
                <div key={g.id} className="relative group rounded-xl overflow-hidden aspect-video bg-neutral-100 dark:bg-neutral-800">
                  <img src={uploadUrl(g.image)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => deleteGalleryImage(g.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Hapus foto"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {galleryPreviews.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {galleryPreviews.map((src, idx) => (
                <div key={idx} className="relative group rounded-xl overflow-hidden aspect-video bg-neutral-100 dark:bg-neutral-800 ring-2 ring-forest-500/50">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePendingGallery(idx)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-amber-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Batalkan"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer ${isEdit ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'}`}>
              <span className="material-symbols-outlined text-base">add_photo_alternate</span>
              Pilih Foto
              <input type="file" accept="image/*" multiple onChange={onGalleryFiles} disabled={!isEdit} className="hidden" />
            </label>
            {galleryFiles.length > 0 && (
              <button
                type="button"
                onClick={uploadGallery}
                disabled={uploadingGallery}
                className={`px-4 py-2 rounded-xl text-sm font-bold text-white ${MORPH_BTN}`}
                style={{ background: 'var(--accent)' }}
              >
                {uploadingGallery ? 'Mengupload...' : `Upload ${galleryFiles.length} Foto`}
              </button>
            )}
          </div>
          {!isEdit && <p className="text-[11px] text-amber-500 mt-1.5">Simpan artikel terlebih dahulu untuk mengaktifkan upload galeri.</p>}
        </div>

        <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-white">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-forest-500" />
          Tampilkan portofolio ini
        </label>

        <div className="flex gap-3 pt-2">
          <button disabled={saving} type="submit" className={`px-6 py-3 rounded-xl font-bold text-white text-sm ${MORPH_BTN}`} style={{ background: 'var(--accent)' }}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
          <button type="button" onClick={() => navigate('/admin/portfolio')} className={`px-6 py-3 rounded-xl font-bold text-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-white ${MORPH_BTN}`}>Batal</button>
        </div>
      </form>
    </AdminLayout>
  );
}